import { SetStateAction, useContext, useMemo } from 'react';
import { DEFAULT_SHARE_SETTINGS_SCOPE_STATE, SHARE_SETTINGS_MODE } from '../../constants';
import type { ShareAccess, ShareSettingsFormState, ShareSettingsInternalContextValue } from '../../types';
import { ShareSettingsInternalContext } from '../components/ShareSettingsInternalContext';
import { ShareSettingsScopedContext } from '../components/ShareSettingsScopedContext';

export function useInternalShareSettings(scopeId?: string): ShareSettingsInternalContextValue {
    const providerContext = useContext(ShareSettingsInternalContext);
    const scopedIdFromContext = useContext(ShareSettingsScopedContext);

    if (!providerContext) {
        throw new Error('useInternalShareSettings must be used within a ShareSettingsInternalStateMutationContext');
    }

    const resolvedScopeId = scopeId ?? scopedIdFromContext;

    if (!resolvedScopeId) {
        throw new Error(
            'useInternalShareSettings requires a scopeId argument or to be used within a ShareSettingsScopedContext'
        );
    }

    const internalShareSettings: ShareSettingsInternalContextValue = useMemo(() => {
        const scopeState = providerContext.scopes[resolvedScopeId] ?? DEFAULT_SHARE_SETTINGS_SCOPE_STATE;

        const mode = scopeState.mode;

        const shareSettings = scopeState.currentShareSettings;

        const enableNotification = providerContext.enableNotification;

        const updateShareSettingsState = (settings) =>
            providerContext.updateShareSettingsState(resolvedScopeId, settings);

        const initialShareSettings =
            mode === SHARE_SETTINGS_MODE.create ? scopeState.committedShareSettings : scopeState.initialShareSettings;

        const initializeShareSettingsScope = (payload) =>
            providerContext.initializeShareSettingsScope(resolvedScopeId, payload);

        const shareAccessEntryForEntityRecordOwner = scopeState.shareAccessEntryForEntityRecordOwner;

        const shareAccessEntryForLoggedInUser = scopeState.shareAccessEntryForLoggedInUser;

        const backedUpSharedWith = scopeState.backedUpSharedWith;

        const setBackedUpSharedWith = (value: SetStateAction<ShareAccess[]>) =>
            providerContext.setBackedUpSharedWith(resolvedScopeId, value);

        const backedUpSharedWithDepartments = scopeState.backedUpSharedWithDepartments;

        const setBackedUpSharedWithDepartments = (value: SetStateAction<ShareAccess[]>) =>
            providerContext.setBackedUpSharedWithDepartments(resolvedScopeId, value);

        const shareSettingsFormState = scopeState.shareSettingsFormState;

        const setShareSettingsFormState = (value: SetStateAction<ShareSettingsFormState>) =>
            providerContext.setShareSettingsFormState(resolvedScopeId, value);

        const internalShareSettings: ShareSettingsInternalContextValue = {
            shareSettings,
            enableNotification,
            updateShareSettingsState,
            initialShareSettings,
            initializeShareSettingsScope,
            shareAccessEntryForEntityRecordOwner,
            shareAccessEntryForLoggedInUser,
            backedUpSharedWith,
            setBackedUpSharedWith,
            backedUpSharedWithDepartments,
            setBackedUpSharedWithDepartments,
            shareSettingsFormState,
            setShareSettingsFormState
        };

        return internalShareSettings;
    }, [providerContext, resolvedScopeId]);

    return internalShareSettings;
}
